import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import { Badge } from 'reactstrap';
import { IoCreateOutline } from 'react-icons/io5';
import { BsTrash3 } from 'react-icons/bs';
import SidebarLayout from '../../../../layouts/SidebarLayout';
import sidebar from '../../utils/sidebar';
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import DefaultButton from "../../../../components/Buttons/Default";
import DefaultPagination from '../../../../components/Pagination';
import AgGridTable from '../../../../components/Tables/AgGrid';
import { get } from '../../../../utils/helpers/api';
import { useSettings } from '../../../../context/settings';
import { documentTypeLabel } from '../../utils/documents';
import IconButton from '../../../../components/Buttons/IconButton';
import { TfiFilter } from 'react-icons/tfi';
import FiltersDrawer from './Drawers/Filters';

function ActionsCellRenderer({ data, context }) {
  return (
    <div className="d-flex align-items-center gap-2 h-100">
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          context.navigate(`/hr/employees/edit/${data.id}`, { state: { employeeData: data } });
        }}
        size="sm" outline color="secondary"
      >
        <IoCreateOutline />
      </IconButton>
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          context.setEmployeeToDelete(data.id);
        }}
        size="sm" outline color="danger"
      >
        <BsTrash3 />
      </IconButton>
    </div>
  );
}

function DocumentsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const { formatDate } = useSettings();

  const [rowData, setRowData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [typeFilter, setTypeFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const FILTER_KEYS = ['document_type', 'added_after', 'added_before'];

  const [filters, setFilters] = useState(() => {
    const initial = {};
    FILTER_KEYS.forEach(key => {
      const val = searchParams.get(key);
      if (val) initial[key] = val;
    });
    return initial;
  });

  const filtersCount = Object.values(filters).filter(v => v).length;

  const getDocuments = async () => {
    setLoading(true);
    let query = '/hr/documents?page=' + currentPage + '&page_size=' + pageSize;
    const activeFilters = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    if (Object.keys(activeFilters).length) {
      query += '&' + new URLSearchParams(activeFilters).toString();
    }
    const data = await get(query);
    if (data?.success) {
      setRowData(data.documents.data);
      setTotalPages(data.documents.last_page);
      setTotalRecords(data.documents.total);
    }
    setLoading(false);
  };

  useEffect(() => {
    getDocuments();
  }, [currentPage, pageSize, filters]);

  const handleApplyFilters = (filtersData) => {
    const activeFilters = Object.fromEntries(Object.entries(filtersData).filter(([, v]) => v));
    setFilters(activeFilters);
    setSearchParams(activeFilters);
    setCurrentPage(1);
    setFiltersOpen(false);
  }

  const columnDefs = useMemo(() => [
    { field: 'reference', headerName: t('hr.documents.reference'), width: 150, sortable: true },
    {
      field: 'type',
      headerName: t('hr.documentType'),
      flex: 1,
      minWidth: 240,
      sortable: true,
      valueFormatter: (p) => documentTypeLabel(p.value)
    },
    {
      field: 'employee_id',
      headerName: t('hr.employeeName'),
      width: 190,
      valueFormatter: (p) => p.data?.employee
        ? `${p.data.employee.firstname} ${p.data.employee.lastname ?? ''}`.trim()
        : '—'
    },
    {
      field: 'issue_date',
      headerName: t('hr.documents.issueDate'),
      width: 140,
      sortable: true,
      valueFormatter: (p) => formatDate(p.value)
    },
    {
      field: 'status',
      headerName: t('common.status'),
      width: 120,
      sortable: true,
      cellRenderer: (p) => (
        <Badge color={p.value === 'cancelled' ? 'danger' : 'success'}>
          {t(`hr.documents.status.${p.value}`)}
        </Badge>
      )
    }
  ], [t, formatDate]);

  const defaultColDef = useMemo(() => ({ resizable: true }), []);
  const gridContext = useMemo(() => ({ navigate, setDocumentToDelete }), [navigate]);

  const onRowClicked = (event) => {
    if (event.event?.target?.closest('button')) return;
    navigate(`/hr/documents/view/${event.data.id}`);
  };

  return (
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader title={t('sidebar.documents')} onReload={getDocuments} dataTotal={totalRecords}>
          <button className="subheader-btn" type="button" onClick={() => setFiltersOpen(true)}>
            <TfiFilter size={13} />
            {t('common.filter')}
            {filtersCount > 0 && (
              <Badge color="primary" className="filter-badge">{filtersCount}</Badge>
            )}
          </button>
          <DefaultButton color="primary" size="sm" onClick={() => navigate('/hr/documents/create')}>
            + {t('hr.documents.newDocument')}
          </DefaultButton>
        </PageSubHeader>

        <div className="subheader-page-content">
          <AgGridTable
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            animateRows={true}
            loading={loading}
            context={gridContext}
            suppressCellFocus={true}
            onRowClicked={onRowClicked}
          />

          <DefaultPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      </div>

      <FiltersDrawer
        isOpen={filtersOpen}
        toggle={() => setFiltersOpen(false)}
        filters={filters}
        applyFilters={handleApplyFilters}
      />
    </SidebarLayout>
  );
}

export default DocumentsPage;
